import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, HandHeart, HeartHandshake, LifeBuoy, Sparkles, Users, Handshake, Gift, CalendarHeart } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { PhotoPlaceholder } from "../components/PhotoPlaceholder";
import { CrisisActions } from "../components/CrisisActions";
import { ComingSoonBadge } from "../components/ComingSoon";
import { HOME_PAGE, EVENT, IMAGES } from "../content/site";

const btnPrimary =
  "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-sage px-7 py-3 text-sm font-semibold text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-sage-light";
const btnOutline =
  "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-sage/30 px-7 py-3 text-sm font-semibold text-sage transition-all duration-300 hover:-translate-y-0.5 hover:bg-sage-soft";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Hero = () => {
  const reduce = useReducedMotion();
  const lines = [HOME_PAGE.heroLine1, HOME_PAGE.heroLine2, HOME_PAGE.heroLine3];
  return (
    <section className="relative overflow-hidden bg-sage" aria-labelledby="home-hero-heading">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-gold/15 blur-3xl" />
        <div className="absolute -right-20 bottom-10 h-80 w-80 rounded-full bg-plum/25 blur-3xl" />
      </div>

      {IMAGES.heroBannerUrl ? (
        <motion.img
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          src={IMAGES.heroBannerUrl}
          alt={IMAGES.heroBannerAlt}
          className="relative h-auto w-full object-contain"
          loading="eager"
          data-testid="home-hero-image"
        />
      ) : null}

      <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-12 sm:px-8 sm:pb-20 sm:pt-14 lg:px-16">
        <motion.p
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="mb-6 text-xs font-semibold uppercase tracking-[0.3em] text-gold sm:text-sm"
        >
          {HOME_PAGE.heroEyebrow}
        </motion.p>
        <h1
          id="home-hero-heading"
          className="max-w-4xl font-serif text-4xl font-light leading-[1.12] tracking-tight text-cream sm:text-5xl lg:text-6xl"
        >
          {lines.map((line, i) => (
            <span className="mask-line" key={i}>
              <motion.span
                className="block"
                initial={reduce ? false : { y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.215, 0.61, 0.355, 1] }}
              >
                {i === 2 ? <em className="text-gold">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-7 max-w-xl text-lg leading-relaxed text-cream/85 sm:text-xl"
        >
          {HOME_PAGE.heroSubtext}
        </motion.p>
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-10 flex flex-wrap gap-3"
        >
          {HOME_PAGE.heroButtons.map((b, i) => (
            <Link
              key={i}
              to={b.to}
              className={
                i === 0
                  ? "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-gold px-7 py-3 text-sm font-semibold text-ink transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-soft"
                  : "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-cream/40 px-7 py-3 text-sm font-semibold text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-cream/10"
              }
              data-testid={`hero-${slug(b.label)}-button`}
            >
              {b.label}
              {i === HOME_PAGE.heroButtons.length - 1 && i > 0 && (
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              )}
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const PathCard = ({ icon: Icon, title, text, to, linkLabel, testId }) => (
  <Reveal>
    <Link
      to={to}
      className="group flex h-full flex-col rounded-2xl border border-sage/10 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-lg sm:p-8"
      data-testid={testId}
    >
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-sage-soft text-sage transition-colors duration-300 group-hover:bg-gold-soft group-hover:text-gold-dark">
        <Icon className="h-6 w-6" aria-hidden="true" />
      </span>
      <h3 className="mt-5 font-serif text-xl font-medium text-ink sm:text-2xl">{title}</h3>
      <p className="mt-2 flex-1 text-base leading-relaxed text-body">{text}</p>
      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-sage">
        {linkLabel}
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
      </span>
    </Link>
  </Reveal>
);

const Section = ({ children, className = "", id, labelledBy }) => (
  <section id={id} aria-labelledby={labelledBy} className={`px-4 py-16 sm:px-8 sm:py-24 lg:px-16 ${className}`}>
    <div className="mx-auto max-w-7xl">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-gold-dark sm:text-sm">{children}</p>
);

const H2 = ({ id, children }) => (
  <h2 id={id} className="font-serif text-2xl font-normal leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
    {children}
  </h2>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />

      {HOME_PAGE.showChoose !== false && (
        <Section labelledBy="choose-heading">
          <Reveal>
            <Eyebrow>Start here</Eyebrow>
            <H2 id="choose-heading">Choose what you need</H2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
            <PathCard
              icon={LifeBuoy}
              title="I need support"
              text="Find crisis and mental-health resources."
              to="/support"
              linkLabel="Find support"
              testId="path-card-need-support"
            />
            <PathCard
              icon={Sparkles}
              title="I'm remembering someone"
              text="Find grief and family support."
              to="/support#grief"
              linkLabel="Grief support"
              testId="path-card-remembering"
            />
            <PathCard
              icon={HandHeart}
              title="I want to help"
              text="Walk, volunteer, donate, or partner with TJ's Light."
              to="/get-involved"
              linkLabel="Ways to help"
              testId="path-card-want-to-help"
            />
          </div>
        </Section>
      )}

      {HOME_PAGE.showRemembering !== false && (
        <Section labelledBy="remembering-heading" className="bg-paper/70">
          <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-12 lg:gap-16">
            <Reveal className="md:col-span-5">
              {IMAGES.tjMomUrl ? (
                <figure data-testid="home-tj-photo">
                  <img
                    src={IMAGES.tjMomUrl}
                    alt={IMAGES.tjMomAlt}
                    className="mx-auto w-full max-w-sm object-contain drop-shadow-xl"
                    loading="lazy"
                  />
                  <figcaption className="mt-4 text-center">
                    <p className="font-serif text-lg italic text-body">"His light continues to guide us."</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      TJ and his mother — the heart behind TJ's Light
                    </p>
                  </figcaption>
                </figure>
              ) : (
                <PhotoPlaceholder
                  alt="Family-approved photograph of TJ — to be supplied by the family"
                  badge="In loving memory"
                  quote="His light continues to guide us."
                  ratio="aspect-[4/5]"
                  testId="home-tj-photo"
                />
              )}
            </Reveal>
            <div className="md:col-span-7">
              <Reveal>
                <Eyebrow>Remembering TJ</Eyebrow>
                <H2 id="remembering-heading">More than the way he died</H2>
                <p className="mt-6 max-w-2xl text-lg leading-relaxed text-body">
                  {HOME_PAGE.rememberingText}
                </p>
                <Link to="/about" className={`${btnPrimary} mt-8`} data-testid="home-read-tjs-story-button">
                  Read TJ's story
                </Link>
              </Reveal>
            </div>
          </div>
        </Section>
      )}

      {HOME_PAGE.showMission !== false && (
        <Section labelledBy="mission-heading">
          <Reveal className="mx-auto max-w-3xl text-center">
            <Eyebrow>Our mission</Eyebrow>
            <H2 id="mission-heading">A place to speak openly and find support</H2>
            <p className="mt-6 text-lg leading-relaxed text-body">{HOME_PAGE.missionText}</p>
          </Reveal>
        </Section>
      )}

      {HOME_PAGE.showWalk !== false && (
        <Section labelledBy="walk-heading" className="bg-sage text-cream">
          <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-12 lg:gap-16">
            <div className="md:col-span-7">
              <Reveal>
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-gold-soft sm:text-sm">
                  Walking for Hope
                </p>
                <h2 id="walk-heading" className="font-serif text-2xl font-normal leading-snug tracking-tight text-cream sm:text-3xl lg:text-4xl">
                  Walk in remembrance. Walk for hope.
                </h2>
                <p className="mt-6 max-w-2xl text-lg leading-relaxed text-cream/85">
                  {HOME_PAGE.walkText}
                </p>
                <div className="mt-6">
                  <ComingSoonBadge
                    label={EVENT.date ? `${EVENT.date}${EVENT.startTime ? ` · ${EVENT.startTime}` : ""}` : "Event details coming soon"}
                  />
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/walking-for-hope#details"
                    className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-gold px-7 py-3 text-sm font-semibold text-ink transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-soft"
                    data-testid="home-event-details-button"
                  >
                    <CalendarHeart className="h-4 w-4" aria-hidden="true" /> Event details
                  </Link>
                  <Link
                    to="/walking-for-hope#register"
                    className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-cream/40 px-7 py-3 text-sm font-semibold text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-cream/10"
                    data-testid="home-register-button"
                  >
                    Register
                  </Link>
                </div>
              </Reveal>
            </div>
            <Reveal className="md:col-span-5" delay={0.1}>
              <figure>
                <img
                  src={IMAGES.walkVenueUrl}
                  alt={IMAGES.walkVenueAlt}
                  className="w-full rounded-2xl border border-cream/20 shadow-xl"
                  loading="lazy"
                  data-testid="home-walk-venue-image"
                />
                <figcaption className="mt-3 text-center text-sm text-cream/70">
                  The Mou Latte in Mamou — where Walking for Hope begins
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </Section>
      )}

      {HOME_PAGE.showInvolved !== false && (
        <Section labelledBy="involved-heading">
          <Reveal>
            <Eyebrow>Get involved</Eyebrow>
            <H2 id="involved-heading">There's a place for you here</H2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Users, title: "Volunteer", to: "/get-involved#volunteer", testId: "involved-card-volunteer" },
              { icon: Handshake, title: "Become a sponsor", to: "/get-involved#sponsor", testId: "involved-card-sponsor" },
              { icon: Gift, title: "Donate", to: "/get-involved#donate", testId: "involved-card-donate" },
              { icon: HeartHandshake, title: "Partner with us", to: "/get-involved#partner", testId: "involved-card-partner" },
            ].map(({ icon: Icon, title, to, testId }, i) => (
              <Reveal key={title} delay={i * 0.08}>
                <Link
                  to={to}
                  className="group flex h-full flex-col items-start rounded-2xl border border-sage/10 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-md"
                  data-testid={testId}
                >
                  <Icon className="h-7 w-7 text-gold-dark" aria-hidden="true" />
                  <span className="mt-4 font-serif text-xl font-medium text-ink">{title}</span>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-sage">
                    Learn more
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </Section>
      )}

      {HOME_PAGE.showClosing !== false && (
        <Section labelledBy="closing-heading" className="bg-gold-soft/40">
          <Reveal className="mx-auto max-w-3xl text-center">
            <h2 id="closing-heading" className="font-serif text-3xl font-light leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
              {HOME_PAGE.closingLine1} <em className="text-sage">{HOME_PAGE.closingLine2}</em>
            </h2>
            <p className="mt-6 text-lg text-body">
              If you're in the United States and need someone to talk to, support is free
              and available around the clock.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/support" className={btnPrimary} data-testid="closing-get-help-button">
                Get Help Now
              </Link>
              <CrisisActions />
            </div>
          </Reveal>
        </Section>
      )}
    </>
  );
}
